import { useUserStore } from '../../../store/useUserStore';
import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { usePostPrivateQnaMutation } from '../../../queries';
import { TextField, Button } from '@mui/material';
import FullHeightContainer from '../../../component/common/FullHeightContainer';
import TopHeaderWithPrev from '../../../component/common/TopHeaderWithPrev';
import { useTranslation } from '../../../hook/useTranslation';

function PrivateQna() {
  const navigate = useNavigate();
  const user = useUserStore();
  const { t } = useTranslation();
  const [title, setTitle] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [content, setContent] = useState<string>('');
  const [focusField, setFocusField] = useState<string | null>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (user) {
      setPhone(user?.phoneNumber || '');
    }
  }, [user]);

  useEffect(() => {
    // 컴포넌트 마운트 시 제목 필드에 포커스
    if (titleInputRef.current) {
      setTimeout(() => {
        titleInputRef.current?.focus();
      }, 100);
    }
  }, []);

  const { mutate: postPrivateQna } = usePostPrivateQnaMutation();

  const handleSubmit = useCallback(() => {
    postPrivateQna(
      { title, phoneNumber: phone, content },
      {
        onSuccess: () => {
          toast.success(t('my.qna.toast_success'));
          navigate(-1);
        },
        onError: (error) => {
          toast.error(t('my.qna.toast_fail'));
          console.error('문의 등록 실패:', error);
        },
      }
    );
  }, [title, phone, content, navigate, postPrivateQna, t]);

  return (
    <FullHeightContainer>
      <TopHeaderWithPrev title={t('my.qna.private_title')} onClickPrev={() => navigate(-1)} />
      <div className="pageInner">
        <div className="w-full py-5 flex flex-col items-center justify-start gap-7">
          <TextField
            inputRef={titleInputRef}
            label={t('my.qna.title_label')}
            variant="standard"
            fullWidth
            value={title}
            onChange={(e) => {
              if (e.target.value.length <= 100) {
                setTitle(e.target.value);
              }
            }}
            sx={{ mb: 2 }}
            placeholder={t('my.qna.title_placeholder')}
            inputProps={{ maxLength: 100 }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label={t('my.qna.phone_label')}
            variant="standard"
            fullWidth
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            sx={{ mb: 2 }}
            disabled
          />
          <div className="relative w-full flex flex-col items-start justify-start gap-2">
            <div
              className="font-normal text-[12px]"
              style={{ color: focusField === 'content' ? '#5EBEBB' : '#363636' }}
            >
              {t('my.qna.content_label')}
            </div>
            <TextField
              placeholder={t('my.qna.content_placeholder')}
              variant="outlined"
              fullWidth
              multiline
              rows={10}
              value={content}
              onChange={(e) => {
                if (e.target.value.length <= 1000) {
                  setContent(e.target.value);
                }
              }}
              onFocus={() => setFocusField('content')}
              onBlur={() => setFocusField(null)}
              sx={{ mb: 2 }}
              inputProps={{ maxLength: 1000 }}
            />
            <div className="absolute right-2 bottom-5 text-[12px] text-[#767676]">
              {content.length}/1000
            </div>
          </div>
        </div>
      </div>
      <div className="w-full flex items-center p-5 flex-col justify-start">
        <Button
          variant="contained"
          fullWidth
          onClick={handleSubmit}
          disabled={!title || !phone || !content}
        >
          {t('my.qna.submit_btn')}
        </Button>
      </div>
    </FullHeightContainer>
  );
}

export default PrivateQna;
